import { EditOutlined, EyeOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Divider, Flex, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { MenuProps } from 'antd/lib'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useInstitutionServiceDeleteDeviceEndpoint,
  useInstitutionServiceGetDeviceListEndpoint,
  useInstitutionServiceGetDeviceListEndpointKey
} from 'src/api/queries'
import { ApiError, GetDeviceListResponseItem } from 'src/api/requests'
import {
  DeleteConfirm,
  message,
  PageBreadcrumbs,
  SearchBar,
  TableActionButtons
} from 'src/components'
import { useTablePagination, useUser } from 'src/hooks'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, resetPagination } from 'src/utils'

import { EquipmentModal } from '../components'

export const EquipmentsPage = () => {
  const { t, currentLang } = useLocales()
  const { userData } = useUser()
  const navigate = useNavigate()

  const pagination = useTablePagination()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState<string>()
  const [isOpen, setIsOpen] = useState(false)
  const [currentId, setCurrentId] = useState<string>()

  const queryParams = {
    pageNumber: Number(pagination.current),
    pageSize: Number(pagination.pageSize),
    institutionsIds: [String(userData?.currentLegalEntityId)],
    ...(!!search && {
      searchTerm: search
    })
  }

  const { data, isLoading } =
    useInstitutionServiceGetDeviceListEndpoint(queryParams)

  const { mutate } = useInstitutionServiceDeleteDeviceEndpoint({
    onError: (e: ApiError) => formErrorHandler(e.body.errors),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useInstitutionServiceGetDeviceListEndpointKey]
      })
      message.success(t('equipments.delete_success_equipment'))
    }
  })

  const onSearch = (val: string) => {
    setSearch(val)
    resetPagination(pagination)
  }

  const onToggle = () => setIsOpen((prev) => !prev)

  const onCloseModal = () => {
    onToggle()
    setCurrentId(undefined)
  }

  const getItems = (id: string): MenuProps['items'] => [
    {
      key: 'details',
      icon: <EyeOutlined />,
      label: t('common.details'),
      onClick: () => navigate(paths.equipments.details(id))
    },
    {
      key: 'edit',
      icon: <EditOutlined />,
      label: t('common.edit'),
      onClick: () => {
        setCurrentId(id)
        onToggle()
      }
    },
    {
      type: 'divider'
    },
    {
      key: 'delete',
      label: (
        <DeleteConfirm
          title={t('equipments.delete_equipment')}
          onConfirm={() =>
            mutate({
              id: String(id)
            })
          }
        />
      ),
      danger: true
    }
  ]

  const columns: ColumnsType<GetDeviceListResponseItem> = [
    {
      key: 'code',
      title: t('equipments.code'),
      dataIndex: 'code'
    },
    {
      key: 'name',
      title: t('medicine_register.products.name'),
      render: (_, { name }) => name?.[currentLang.value]
    },
    {
      key: 'name',
      title: t('common.type'),
      render: (_, { type }) => type?.name?.[currentLang.value]
    },
    {
      key: 'last inspection date',
      title: t('equipments.last_inspection_date'),
      render: (_, { lastInspectionDate }) =>
        lastInspectionDate
          ? dayjs.utc(lastInspectionDate).tz().format(dayMonthYearFormatWithDot)
          : '-'
    },
    {
      key: 'actions',
      render: ({ id }) => (
        <TableActionButtons justify="center" items={getItems(id)} />
      )
    }
  ]

  return (
    <PageBreadcrumbs
      title={t('menu.equipments')}
      items={[{ title: t('menu.equipments') }]}
    >
      <Flex vertical>
        <Flex justify="space-between">
          <SearchBar
            onSearch={onSearch}
            placeholder={t('equipments.search_placeholder')}
          />

          <Button onClick={onToggle} type="primary">
            {t('equipments.add_new_equipment')}
          </Button>
        </Flex>

        <Divider />

        <Table
          rowKey="id"
          bordered
          loading={isLoading}
          columns={columns}
          dataSource={data?.data}
          scroll={{ x: 'max-content' }}
          pagination={{ ...pagination, total: 0 }}
        />
      </Flex>

      {isOpen && <EquipmentModal id={currentId} onCancel={onCloseModal} />}
    </PageBreadcrumbs>
  )
}
